class googleMap extends HTMLElement {
  constructor() {
    super()
    this.map = null
    this.markerCluster = null
    this.markers = []
    this.locations = []
    this.filtered = []
    this.lastCoords = null
    this.searchTimeout = null
    this.userLocated = false
    this.loadingDotsInterval = null
    this.autocompleteResults = []
    this.resultsOffset = 0
    this.resultsPerPage = 20
    this.lastResults = []
    this.addressManuallyTyped = true
    this.geocodeCache = new Map()
    this.reverseGeocodeCache = new Map()
    this.nearbyStoreCache = new Map()
    this.currentLocale =
      (typeof Shopify !== 'undefined' && Shopify.locale) || 'es'
    this.countryNameTranslations = {
      es: {
        de: 'Alemania',
        be: 'Bélgica',
        ca: 'Canadá',
        cy: 'Chipre',
        hr: 'Croacia',
        es: 'España',
        fr: 'Francia',
        gr: 'Grecia',
        gf: 'Guayana Francesa',
        ie: 'Irlanda',
        it: 'Italia',
        lu: 'Luxemburgo',
        ma: 'Marruecos',
        mx: 'México',
        nl: 'Países Bajos',
        pf: 'Polinesia Francesa',
        pl: 'Polonia',
        gb: 'Reino Unido',
        cz: 'República Checa',
        za: 'Sudáfrica',
        ch: 'Suiza',
        us: 'Estados Unidos'
      },
      fr: {
        de: 'Allemagne',
        be: 'Belgique',
        ca: 'Canada',
        cy: 'Chypre',
        hr: 'Croatie',
        es: 'Espagne',
        fr: 'France',
        gr: 'Grèce',
        gf: 'Guyane Française',
        ie: 'Irlande',
        it: 'Italie',
        lu: 'Luxembourg',
        ma: 'Maroc',
        mx: 'Mexique',
        nl: 'Pays-Bas',
        pf: 'Polynésie Française',
        pl: 'Pologne',
        gb: 'Royaume-Uni',
        cz: 'République Tchèque',
        za: 'Afrique du Sud',
        ch: 'Suisse',
        us: 'États-Unis'
      },
      en: {
        de: 'Germany',
        be: 'Belgium',
        ca: 'Canada',
        cy: 'Cyprus',
        hr: 'Croatia',
        es: 'Spain',
        fr: 'France',
        gr: 'Greece',
        gf: 'French Guiana',
        ie: 'Ireland',
        it: 'Italy',
        lu: 'Luxembourg',
        ma: 'Morocco',
        mx: 'Mexico',
        nl: 'Netherlands',
        pf: 'French Polynesia',
        pl: 'Poland',
        gb: 'United Kingdom',
        cz: 'Czech Republic',
        za: 'South Africa',
        ch: 'Switzerland',
        us: 'USA'
      },
      it: {
        de: 'Germania',
        be: 'Belgio',
        ca: 'Canada',
        cy: 'Cipro',
        hr: 'Croazia',
        es: 'Spagna',
        fr: 'Francia',
        gr: 'Grecia',
        gf: 'Guiana Francese',
        ie: 'Irlanda',
        it: 'Italia',
        lu: 'Lussemburgo',
        ma: 'Marocco',
        mx: 'Messico',
        nl: 'Paesi Bassi',
        pf: 'Polinesia Francese',
        pl: 'Polonia',
        gb: 'Regno Unito',
        cz: 'Repubblica Ceca',
        za: 'Sudafrica',
        ch: 'Svizzera',
        us: 'Stati Uniti'
      },
      pt: {
        de: 'Alemanha',
        be: 'Bélgica',
        ca: 'Canadá',
        cy: 'Chipre',
        hr: 'Croácia',
        es: 'Espanha',
        fr: 'França',
        gr: 'Grécia',
        gf: 'Guiana Francesa',
        ie: 'Irlanda',
        it: 'Itália',
        lu: 'Luxemburgo',
        ma: 'Marrocos',
        mx: 'México',
        nl: 'Países Baixos',
        pf: 'Polinésia Francesa',
        pl: 'Polónia',
        gb: 'Reino Unido',
        cz: 'República Checa',
        za: 'África do Sul',
        ch: 'Suíça',
        us: 'Estados Unidos'
      },
      de: {
        de: 'Deutschland',
        be: 'Belgien',
        ca: 'Kanada',
        cy: 'Zypern',
        hr: 'Kroatien',
        es: 'Spanien',
        fr: 'Frankreich',
        gr: 'Griechenland',
        gf: 'Französisch-Guayana',
        ie: 'Irland',
        it: 'Italien',
        lu: 'Luxemburg',
        ma: 'Marokko',
        mx: 'Mexiko',
        nl: 'Niederlande',
        pf: 'Französisch-Polynesien',
        pl: 'Polen',
        gb: 'Vereinigtes Königreich',
        cz: 'Tschechien',
        za: 'Südafrika',
        ch: 'Schweiz',
        us: 'USA'
      }
    }

    const translationScript = document.getElementById('google-maps-traduction')
    try {
      this.translations = translationScript
        ? JSON.parse(translationScript.textContent)
        : {}
    } catch (e) {
      this.translations = {}
      console.warn('Error al parsear traducciones', e)
    }
  }

  getTranslation(key, params = {}) {
    let text = this.translations?.[key]

    if (typeof text !== 'string') {
      console.warn(`Traducción no encontrada o inválida para: "${key}"`)
      return key
    }

    // Reemplazo de parámetros {count}, etc.
    for (const [k, v] of Object.entries(params)) {
      text = text.replaceAll(`{${k}}`, v)
    }

    return text
  }

  clearCaches() {
    this.geocodeCache.clear()
    this.reverseGeocodeCache.clear()
    this.nearbyStoreCache.clear()
  }

  connectedCallback() {
    this.render()
    this.initData()
    this.initMap()
    this.requestUserLocation()
    this.initDraggableFilters()
    this.setupAddressAutocomplete()
  }

  render() {
    this.countrySelect = this.querySelector('#country')
    this.cityInput = this.querySelector('#city')
    this.addressInput = this.querySelector('#address')
    this.distanceInput = this.querySelector('#distance')
    this.rangeValue = this.querySelector('#range-value')
    this.messageBox = this.querySelector('#message')
    this.loadingBox = this.querySelector('#loading')
    this.resultsList = this.querySelector('#results-list')
    this.distanceLabel = this.querySelector('#distance-wrapper')
    this.inputClearable = this.querySelectorAll('.input-clearable')

    this.countrySelect.addEventListener('change', () => {
      this.clearCaches()
      this.cityInput.value = ''
      this.addressInput.value = ''
      this.lastCoords = null
      this.filterData()
      this.toggleDistanceVisibility()
      this.updateClearButtonsVisibility()
    })

    this.cityInput.addEventListener('input', () => {
      this.lastCoords = null
      this.debouncedSearch({ source: 'city' })
      this.toggleDistanceVisibility()
      this.updateClearButtonsVisibility()
    })

    this.distanceInput.addEventListener('input', () => {
      this.rangeValue.textContent = this.distanceInput.value
      this.debouncedSearch({ source: 'distance' })
    })

    this.resultsList.addEventListener('scroll', () => this.handleScrollLoad())

    // Asignar eventos a los botones de limpiar (una sola vez)
    this.inputClearable.forEach((wrapper) => {
      const input = wrapper.querySelector('input, select')
      const clearBtn = wrapper.querySelector('.clear-btn')

      if (!input || !clearBtn) return

      input.addEventListener('input', () => this.updateClearButtonsVisibility())
      input.addEventListener('change', () =>
        this.updateClearButtonsVisibility()
      )

      clearBtn.addEventListener('click', () => {
        if (input.tagName === 'SELECT') {
          input.selectedIndex = 0
        } else {
          input.value = ''
        }

        const allEmpty =
          !this.countrySelect.value &&
          !this.cityInput.value.trim() &&
          !this.addressInput.value.trim()

        if (allEmpty) {
          this.clearCaches() // solo si todo fue limpiado
        }

        this.lastCoords = null
        this.toggleDistanceVisibility()
        this.filterData()
        this.updateClearButtonsVisibility()
      })
    })

    // Visibilidad inicial
    this.updateClearButtonsVisibility()
  }

  translateCountryCode(code) {
    const lang = this.currentLocale || 'es'
    const dict = this.countryNameTranslations?.[lang] || {}
    return dict[code.toLowerCase()] || code.toUpperCase()
  }

  setupAddressAutocomplete() {
    const MIN_CHARS = 3
    const input = this.addressInput
    const suggestionBox = this.querySelector('#address-suggestions')
    const suggestionsCache = new Map()
    const cityViewboxCache = new Map()
    let debounceTimeout = null

    const fetchSuggestions = async (query) => {
      const countryCode = this.countrySelect?.value?.toLowerCase() || ''
      const city = this.cityInput?.value?.trim() || ''
      const fullQuery = [query, city].filter(Boolean).join(', ')
      const cacheKey = `${fullQuery}|${countryCode}`

      if (suggestionsCache.has(cacheKey)) return suggestionsCache.get(cacheKey)

      let url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5&q=${encodeURIComponent(
        fullQuery
      )}`
      if (countryCode) url += `&countrycodes=${countryCode}`

      if (city) {
        const viewboxKey = `${city}|${countryCode}`
        let bounds = cityViewboxCache.get(viewboxKey)

        if (!bounds) {
          bounds = await getCityViewbox(city, countryCode)
          if (bounds) cityViewboxCache.set(viewboxKey, bounds)
        }

        if (bounds) {
          url += `&viewbox=${bounds.viewbox}&bounded=1`
        }
      }

      const res = await fetch(url, {
        headers: {
          'Accept-Language': 'es',
          'User-Agent': 'TuSitioWeb/1.0 (contacto@tusitio.com)'
        }
      })

      const data = await res.json()
      suggestionsCache.set(cacheKey, data)
      return data
    }

    const getCityViewbox = async (city, countryCode) => {
      const q = encodeURIComponent(`${city}, ${countryCode}`)
      const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&q=${q}`

      const res = await fetch(url, {
        headers: {
          'Accept-Language': 'es',
          'User-Agent': 'TuSitioWeb/1.0 (contacto@tusitio.com)'
        }
      })

      const [result] = await res.json()
      if (!result || !result.boundingbox) return null

      const [latMin, latMax, lonMin, lonMax] = result.boundingbox.map(Number)
      return {
        viewbox: `${lonMin},${latMax},${lonMax},${latMin}`,
        bounded: 1
      }
    }

    const renderSuggestions = (results) => {
      suggestionBox.innerHTML = ''

      const valid = results
        .map((sug) => {
          const street =
            sug.address?.road ||
            sug.address?.pedestrian ||
            sug.address?.footway ||
            sug.address?.cycleway
          if (!street) return null

          const context = [
            sug.address?.suburb,
            sug.address?.neighbourhood,
            sug.address?.postcode,
            sug.address?.city || sug.address?.town || sug.address?.village
          ]
            .filter(Boolean)
            .join(', ')

          return {
            street: street.trim(),
            context,
            originalData: sug // Guardamos el objeto original para luego extraer ciudad/provincia
          }
        })
        .filter(Boolean)

      if (valid.length === 0) {
        suggestionBox.style.display = 'none'
        return
      }

      // NO rellenar directamente el input si solo hay una sugerencia
      // -> usuario debe confirmar manualmente
      valid.forEach((item) => {
        const li = document.createElement('li')
        li.classList.add('suggestion-item')
        li.dataset.full = item.street
        li.dataset.context = JSON.stringify(item.originalData)
        li.innerHTML = `
      <strong>${item.street}</strong><br>
      <small style="color: #aaa;">${item.context}</small>
    `
        suggestionBox.appendChild(li)
      })

      suggestionBox.style.display = 'block'
    }

    input.addEventListener('input', () => {
      this.addressManuallyTyped = true
      clearTimeout(debounceTimeout)

      const rawQuery = input.value.trim()
      const query = this.expandAbbreviations(rawQuery)

      if (query.length < MIN_CHARS) {
        suggestionBox.innerHTML = ''
        suggestionBox.style.display = 'none'
        return
      }

      debounceTimeout = setTimeout(async () => {
        this.setLoading(input, true)

        try {
          const suggestions = await fetchSuggestions(query)
          renderSuggestions(suggestions)

          // ✅ Aquí es donde se usa:
          if (this.isPostalCode(query)) {
            this.debouncedSearch({ source: 'postalCode' })
          }
        } catch (err) {
          console.warn('Error obteniendo sugerencias de Nominatim', err)
          suggestionBox.innerHTML = ''
          suggestionBox.style.display = 'none'
        } finally {
          this.setLoading(input, false)
        }
      }, 200)
    })

    suggestionBox.addEventListener('click', (e) => {
      const item = e.target.closest('.suggestion-item')
      if (!item) return

      this.addressInput.value = item.dataset.full.trim()
      this.addressManuallyTyped = false

      try {
        const data = JSON.parse(item.dataset.context)
        const city =
          data.address?.city ||
          data.address?.town ||
          data.address?.village ||
          ''
        const state = data.address?.state || ''

        if (this.cityInput && !this.cityInput.value.trim()) {
          this.cityInput.value = city || state
        }
      } catch (err) {
        console.warn('No se pudo extraer ciudad de la sugerencia', err)
      }

      suggestionBox.innerHTML = ''
      suggestionBox.style.display = 'none'

      this.debouncedSearch({ source: 'address' })
      this.updateClearButtonsVisibility()
    })

    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) {
        suggestionBox.innerHTML = ''
        suggestionBox.style.display = 'none'
      }
    })
  }

  expandAbbreviations(query) {
    const dictionary = {
      es: {
        av: 'avenida',
        ave: 'avenida',
        'c/': 'calle',
        cl: 'calle',
        cr: 'carrera',
        ps: 'paseo'
      },
      fr: {
        av: 'avenue',
        bd: 'boulevard',
        r: 'rue'
      },
      en: {
        st: 'street',
        ave: 'avenue',
        blvd: 'boulevard',
        rd: 'road'
      },
      it: {
        via: 'via',
        'v.le': 'viale',
        'p.za': 'piazza'
      },
      pt: {
        av: 'avenida',
        r: 'rua'
      },
      de: {
        str: 'straße',
        allee: 'allee',
        platz: 'platz'
      }
    }

    const lang = (navigator.language || 'es').slice(0, 2) // detectar idioma del navegador
    const langDict = dictionary[lang] || dictionary['es']

    return query
      .split(/\s+/)
      .map((word) => langDict[word.toLowerCase()] || word)
      .join(' ')
  }

  setLoading(inputEl, isLoading = true) {
    const wrapper = inputEl.closest('.input-clearable')
    if (!wrapper) return

    if (isLoading) {
      wrapper.classList.add('loading')
    } else {
      wrapper.classList.remove('loading')
    }
  }

  toggleDistanceVisibility() {
    const address = this.addressInput?.value?.trim() || ''
    const city = this.cityInput?.value?.trim() || ''
    const shouldShow = !!(address || city)
    if (this.distanceLabel) {
      this.distanceLabel.style.display = shouldShow ? 'block' : 'none'
    }
  }

  updateClearButtonsVisibility() {
    this.inputClearable?.forEach((wrapper) => {
      const input = wrapper.querySelector('input, select')
      const clearBtn = wrapper.querySelector('.clear-btn')

      if (!input || !clearBtn) return

      clearBtn.style.display = input.value ? 'block' : 'none'
    })
  }

  initData() {
    const dataScriptId = this.getAttribute('data') || 'google-maps-markers'
    const dataScript = document.getElementById(dataScriptId)
    if (!dataScript) {
      console.error(`No se encontró el script con ID ${dataScriptId}`)
      return
    }

    try {
      const rawLocations = JSON.parse(dataScript.textContent)
      this.locations = rawLocations.map((loc) => ({
        ...loc,
        street: loc.address
      }))
      this.filtered = this.locations

      // Crear mapa de códigos de país -> nombre
      const countryMap = {}
      this.locations.forEach((loc) => {
        if (loc.country_code && !countryMap[loc.country_code.toLowerCase()]) {
          countryMap[loc.country_code.toLowerCase()] = loc.country
        }
      })

      // Función de capitalización
      const capitalize = (str) =>
        str
          .toLocaleLowerCase()
          .split(/([\s\-\(\)]+)/)
          .map((part) =>
            /^[a-záéíóúüñç]/i.test(part)
              ? part.charAt(0).toLocaleUpperCase() + part.slice(1)
              : part
          )
          .join('')

      // Ordenar y agregar al <select>
      const countries = Object.entries(countryMap).sort((a, b) =>
        a[1].localeCompare(b[1])
      )

      this.countrySelect.innerHTML += countries
        .map(([code]) => {
          const translated = this.translateCountryCode(code)
          return `<option value="${code}">${capitalize(translated)}</option>`
        })
        .join('')
    } catch (e) {
      console.error('Error al parsear JSON de tiendas:', e)
    }
  }

  debouncedSearch(options = {}) {
    const address = this.addressInput?.value?.trim() || ''
    const city = this.cityInput?.value?.trim() || ''
    const distance = this.distanceInput?.value?.trim() || ''

    const shouldAllowManualSearch =
      this.isPostalCode(address) ||
      options.source === 'address' ||
      options.source === 'postalCode' ||
      options.source === 'city' ||
      city.length >= 2 ||
      distance.length > 0

    if (this.addressManuallyTyped && !shouldAllowManualSearch) return

    clearTimeout(this.searchTimeout)
    this.searchTimeout = setTimeout(() => this.handleSearch(), 600)
  }

  isPostalCode(value) {
    return /^\d{4,6}$/.test(value)
  }

  loadDefaultStores() {
    this.filterData()
    this.handleSearch()

    this.dispatchEvent(
      new CustomEvent('locationsFiltered', {
        bubbles: true,
        detail: { filtered: this.filtered }
      })
    )
  }

  showMapSpinner() {
    const spinner = this.querySelector('#map-spinner')
    if (spinner) spinner.classList.remove('hidden')
  }

  hideMapSpinner() {
    const spinner = this.querySelector('#map-spinner')
    if (spinner) spinner.classList.add('hidden')
  }

  async requestUserLocation() {
    if (!navigator.geolocation) {
      this.loadDefaultStores()
      return
    }

    this.showMapSpinner() // 👈 mostrar spinner al inicio

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        this.lastCoords = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
        this.userLocated = true
        this.toggleDistanceVisibility(true)

        try {
          const reverseData = await this.reverseGeocodeWithNominatim(
            this.lastCoords.lat,
            this.lastCoords.lng
          )

          const { country_code, city, town, village, state, road } =
            reverseData.address || {}

          const resolvedCity = city || town || village || state || ''
          this.cityInput.value = resolvedCity
          this.addressInput.value = road || ''

          if (!this.countrySelect.value && country_code) {
            const matched = Array.from(this.countrySelect.options).find(
              (opt) => opt.value.toLowerCase() === country_code.toLowerCase()
            )
            if (matched) {
              this.countrySelect.value = matched.value
            }
          }

          this.updateClearButtonsVisibility()
          this.toggleDistanceVisibility()
          this.filterData()
          this.handleSearch()
        } catch (err) {
          console.warn('Reverse geocoding fallido', err)
          this.loadDefaultStores()
        } finally {
          this.hideMapSpinner() // 👈 ocultar spinner
        }
      },
      (err) => {
        console.warn('Ubicación denegada o no disponible', err)
        this.loadDefaultStores()
        this.hideMapSpinner() // 👈 ocultar spinner en error también
      },
      {
        enableHighAccuracy: false,
        timeout: 5000,
        maximumAge: 30000
      }
    )
  }

  cleanAddress(address) {
    if (!address) return ''

    let cleaned = address.trim()

    // Evita modificar direcciones francesas útiles
    const isJustPostal = /^\d{4,6}$/.test(cleaned)
    if (isJustPostal) return cleaned

    // Normaliza (acentos) y minúsculas
    cleaned = cleaned
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()

    // Elimina detalles interiores (piso, puerta, etc.)
    cleaned = cleaned.replace(
      /\b(n[ºo]?\.?|num\.?|número|no\.?)\s*\d+[a-zA-Z]?\b/g,
      ''
    )

    cleaned = cleaned.replace(
      /\b(piso|puerta|planta|segundo|primero|tercero|bajo|local|oficina|dpto|apartamento|apt|étage|appartement|entresuelo|esc\.?|escalier|escalera|étg)\b(\s*\d*[a-zA-Z]?)?/g,
      ''
    )

    cleaned = cleaned.replace(/[\.,;:#]/g, '')

    // Elimina números al final si parecen portal
    cleaned = cleaned.replace(/\s\d{1,5}[a-zA-Z]?\b/g, '')

    // Limpia espacios extra
    cleaned = cleaned.replace(/\s+/g, ' ').trim()

    // Capitaliza
    cleaned = cleaned
      .split(' ')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ')

    return cleaned
  }

  async reverseGeocodeWithNominatim(lat, lon) {
    const key = `${lat.toFixed(4)},${lon.toFixed(4)}`

    if (this.reverseGeocodeCache.has(key))
      return this.reverseGeocodeCache.get(key)

    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`
    const res = await fetch(url, {
      headers: {
        'Accept-Language': 'es',
        'User-Agent': 'TuSitioWeb/1.0 (contacto@tusitio.com)'
      }
    })

    const data = await res.json()
    this.reverseGeocodeCache.set(key, data)
    return data
  }

  async geocodeWithNominatim(query) {
    const countryCode = this.countrySelect.value.toUpperCase()
    const cacheKey = `${query}|${countryCode}`

    if (this.geocodeCache.has(cacheKey)) return this.geocodeCache.get(cacheKey)

    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
      query
    )}&countrycodes=${countryCode}`

    const res = await fetch(url, {
      headers: {
        'Accept-Language': 'es',
        'User-Agent': 'TuSitioWeb/1.0 (contacto@tusitio.com)'
      }
    })

    const data = await res.json()
    if (!data.length) throw new Error('Sin resultados')

    const coords = {
      lat: parseFloat(data[0].lat),
      lng: parseFloat(data[0].lon)
    }
    this.geocodeCache.set(cacheKey, coords)
    return coords
  }

  haversineDistance(lat1, lon1, lat2, lon2) {
    const toRad = (deg) => (deg * Math.PI) / 180
    const R = 6371
    const dLat = toRad(lat2 - lat1)
    const dLon = toRad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  getNearbyStores(userLat, userLon, stores, maxDistanceKm = 60) {
    const key = `${userLat.toFixed(4)},${userLon.toFixed(4)}|${maxDistanceKm}`

    if (this.nearbyStoreCache.has(key)) return this.nearbyStoreCache.get(key)

    const result = stores.filter((store) => {
      const d = this.haversineDistance(userLat, userLon, store.lat, store.lng)
      return d <= maxDistanceKm
    })

    this.nearbyStoreCache.set(key, result)
    return result
  }

  async handleSearch() {
    const city = this.cityInput.value.trim()
    const rawAddress = this.addressInput.value.trim()
    const address = this.cleanAddress(rawAddress)

    // Solo asignar país por defecto si hay ciudad o dirección y país está vacío
    if (!this.countrySelect.value && (city || address)) {
      const defaultCode = 'ES'
      const defaultOption = Array.from(this.countrySelect.options).find(
        (opt) => opt.value.toUpperCase() === defaultCode
      )
      if (defaultOption) {
        this.countrySelect.value = defaultOption.value
        this.updateClearButtonsVisibility()
      }
    }

    const countryCode = this.countrySelect.value.toLowerCase()
    const selectedCountryName = this.countryNameMap?.[countryCode] || ''

    const queryParts = [address, city]
    if (selectedCountryName) queryParts.push(selectedCountryName)
    const query = queryParts.filter(Boolean).join(', ')

    const maxDistance = Math.min(
      Math.max(parseInt(this.distanceInput.value, 10) || 60, 10),
      100
    )

    const onlyFilteringByCountry = !address && !city && !this.lastCoords

    if (onlyFilteringByCountry) {
      this.messageBox.innerHTML = this.getTranslation(
        'showingStoresForCountry',
        {
          count: this.filtered.length
        }
      )
      this.lastResults = this.filtered
      this.resultsOffset = 0
      this.renderResultsList(this.lastResults)
      this.dispatchEvent(
        new CustomEvent('locationsFiltered', {
          bubbles: true,
          detail: { filtered: this.filtered }
        })
      )
      return
    }

    if (this.lastCoords && !query) {
      const nearby = this.getNearbyStores(
        this.lastCoords.lat,
        this.lastCoords.lng,
        this.filtered,
        maxDistance
      )
      this.messageBox.innerHTML = this.getTranslation(
        'showingStoresForCountry',
        {
          count: nearby.length
        }
      )
      this.lastResults = nearby
      this.resultsOffset = 0
      this.renderResultsList(this.lastResults)
      this.dispatchEvent(
        new CustomEvent('locationsFiltered', {
          bubbles: true,
          detail: { filtered: nearby }
        })
      )
      return
    }

    try {
      if (!this.lastCoords && query) {
        this.lastCoords = await this.geocodeWithNominatim(query)
      }

      if (!this.lastCoords) throw new Error('No se pudo obtener coordenadas.')

      const nearby = this.getNearbyStores(
        this.lastCoords.lat,
        this.lastCoords.lng,
        this.filtered,
        maxDistance
      )

      if (nearby.length > 0) {
        this.messageBox.innerHTML = this.getTranslation(
          'showingStoresForCountry',
          {
            count: nearby.length
          }
        )
        this.lastResults = nearby
        this.resultsOffset = 0
        this.renderResultsList(this.lastResults)
        this.dispatchEvent(
          new CustomEvent('locationsFiltered', {
            bubbles: true,
            detail: { filtered: nearby }
          })
        )
      } else {
        const fallback = this.filtered
        this.messageBox.innerHTML =
          this.getTranslation('noValidStoresToShow') +
          ' </br>' +
          this.getTranslation('resultsFound', { count: fallback.length })

        this.lastResults = fallback
        this.resultsOffset = 0
        this.renderResultsList(this.lastResults)
        this.dispatchEvent(
          new CustomEvent('locationsFiltered', {
            bubbles: true,
            detail: { filtered: fallback }
          })
        )
      }
    } catch (e) {
      if (e.message === 'Sin resultados') {
        console.info('Dirección no encontrada, usando fallback.')
      } else {
        console.error('Error geolocalizando dirección:', e)
      }

      const fallback = this.filtered
      this.messageBox.innerHTML =
        this.getTranslation('noValidStoresToShow') +
        ' </br>' +
        this.getTranslation('showingStoresForCountry', {
          count: fallback.length
        })

      this.lastResults = fallback
      this.resultsOffset = 0
      this.renderResultsList(this.lastResults)
      this.dispatchEvent(
        new CustomEvent('locationsFiltered', {
          bubbles: true,
          detail: { filtered: fallback }
        })
      )
    }
  }

  filterData() {
    const selectedCountryCode = this.countrySelect.value.toLowerCase()
    this.filtered = selectedCountryCode
      ? this.locations.filter(
          (loc) => loc.country_code?.toLowerCase() === selectedCountryCode
        )
      : this.locations

    this.messageBox.textContent = '' // mensaje actualizado solo desde handleSearch
    this.lastCoords = null

    const noCityOrAddress =
      !this.cityInput.value.trim() && !this.addressInput.value.trim()
    if (noCityOrAddress) {
      this.handleSearch() // usa this.filtered sin geolocalizar
    }
  }

  renderResultsList(stores) {
    if (!stores || !Array.isArray(stores)) return

    // Si es la primera página, limpiamos la lista
    if (this.resultsOffset === 0) {
      this.resultsList.innerHTML = `<h2>${this.getTranslation(
        'resultsListHeading'
      )}</h2><ul></ul>`
    }

    const ul = this.resultsList.querySelector('ul')

    // Paginación
    const nextBatch = stores.slice(
      this.resultsOffset,
      this.resultsOffset + this.resultsPerPage
    )

    nextBatch.forEach((store) => {
      const li = document.createElement('li')

      // Crea la estructura del item con info + imagen
      li.innerHTML = `
        <a class="info" href="https://www.google.com/maps?q=${store.lat},${
        store.lng
      }" target="_blank">
          <span>YOKONO STORE</span>
          <h3>${store.name || ''}</h3>
          <p>${store.address || ''}</p>
          <p>${store.zipCode || ''}, ${store.city || ''}</p>
        </a>
        `

      ul.appendChild(li)
    })

    // Incrementa el offset para la siguiente página
    this.resultsOffset += this.resultsPerPage
  }

  handleScrollLoad() {
    if (!this.lastResults || this.resultsOffset >= this.lastResults.length)
      return
    const { scrollTop, scrollHeight, clientHeight } = this.resultsList
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      this.renderResultsList(this.lastResults)
    }
  }

  initDraggableFilters() {
    const panel = this.querySelector('.google-map__filters')
    const handle = panel?.querySelector('.drag-handle')
    if (!panel || !handle) return

    let startY = 0
    let currentY = 0
    let isDragging = false
    let initialDesplazamiento = 0

    // Valor mínimo = desplazamiento inicial (leído de CSS inline en handle)
    const minDesplazamiento =
      parseFloat(
        getComputedStyle(handle).getPropertyValue('--desplazamiento')
      ) || 150

    const getDesplazamiento = () => {
      const value = getComputedStyle(panel).getPropertyValue('--desplazamiento')
      const numeric = parseFloat(value)
      return isNaN(numeric) ? minDesplazamiento : numeric
    }

    const setDesplazamiento = (val) => {
      const max = panel.getBoundingClientRect().height
      const clamped = Math.max(minDesplazamiento, Math.min(val, max))
      panel.style.setProperty('--desplazamiento', `${clamped}px`)
      return clamped
    }

    const onTouchStart = (e) => {
      if (!e.target.closest('.drag-handle')) return
      isDragging = true
      panel.classList.add('dragging')

      startY = e.touches[0].clientY
      currentY = startY
      initialDesplazamiento = getDesplazamiento()
    }

    const onTouchMove = (e) => {
      if (!isDragging) return
      e.preventDefault()

      currentY = e.touches[0].clientY
      const delta = startY - currentY
      const newDesplazamiento = initialDesplazamiento + delta

      setDesplazamiento(newDesplazamiento)
    }

    const onTouchEnd = () => {
      if (!isDragging) return
      isDragging = false
      panel.classList.remove('dragging')

      const delta = startY - currentY
      const max = panel.getBoundingClientRect().height

      const snapTo = delta > 30 ? max : minDesplazamiento
      setDesplazamiento(snapTo)
    }

    handle.addEventListener('touchstart', onTouchStart)
    handle.addEventListener('touchmove', onTouchMove, { passive: false })
    handle.addEventListener('touchend', onTouchEnd)

    // Set initial displacement
    setDesplazamiento(getDesplazamiento())
  }

  getMapPadding() {
    const isDesktop = window.innerWidth >= 1024

    return {
      top: 50,
      bottom: 50,
      left: isDesktop ? window.innerWidth * 0.3 : 50,
      right: 50
    }
  }

  async initMap() {
    const { Map } = await google.maps.importLibrary('maps')

    this.loadingBox.style.display = 'block'

    const icons = JSON.parse(
      document.getElementById('google-maps-settings').textContent
    )
    const bounds = new google.maps.LatLngBounds()
    const infoWindow = new google.maps.InfoWindow()
    const markerDataMap = []

    const styles = [{ featureType: 'poi', stylers: [{ visibility: 'off' }] }]

    this.map = new Map(this.querySelector('#map'), {
      center: { lat: 0, lng: 0 },
      zoom: 2,
      gestureHandling: window.innerWidth < 1024 ? 'greedy' : 'cooperative',
      disableDefaultUI: true,
      mapTypeControl: false,
      mapTypeId: 'roadmap',
      styles
    })

    const validData = this.locations.filter(
      (loc) => isFinite(loc.lat) && isFinite(loc.lng)
    )

    if (validData.length === 0) {
      this.messageBox.textContent = this.getTranslation('noValidCoordinates')
      this.loadingBox.style.display = 'none'
      return
    }

    this.markers = validData.map((location) => {
      const position = new google.maps.LatLng(location.lat, location.lng)
      const markerOptions = {
        position,
        title: location.address
      }
      if (icons.icon) {
        markerOptions.icon = {
          url: icons.icon,
          scaledSize: new google.maps.Size(25, 25),
          anchor: new google.maps.Point(12, 25)
        }
      }
      const marker = new google.maps.Marker({ ...markerOptions, map: null })

      const content = `
        <div class="container_content__icon-map">
          <a href="https://www.google.com/maps?q=${location.lat},${
        location.lng
      }" target="_blank">
            <div class="container__icon-map">
              <h3>${location.name || ''}</h3>
              <p>${location.address || ''}</p>
              <p>${location.zipCode || ''}, ${location.city || ''}</p>
              <button class="button__icon-map">Cómo llegar</button>
            </div>
          </a>
        </div>
      `

      marker.addListener('click', () => {
        infoWindow.setContent(content)
        infoWindow.open(this.map, marker)
      })

      markerDataMap.push({ ...location, marker, content })

      bounds.extend(position)
      return marker
    })

    this.map.fitBounds(bounds, this.getMapPadding())
    this.loadingBox.style.display = 'none'

    google.maps.event.addListenerOnce(this.map, 'bounds_changed', () => {
      const maxZoom = 15
      if (this.map.getZoom() > maxZoom) {
        this.map.setZoom(maxZoom)
      }
    })

    window.addEventListener('locationsFiltered', (e) => {
      const filtered = e.detail.filtered
      if (this.markerCluster) {
        this.markerCluster.clearMarkers()
      }
      this.markers.forEach((marker) => marker.setMap(null))

      const isMatch = (a, b) =>
        a.lat === b.lat &&
        a.lng === b.lng &&
        normalize(a.address) === normalize(b.address) &&
        normalize(a.city) === normalize(b.city) &&
        String(a.zipCode) === String(b.zipCode)

      const normalize = (val) => (val || '').toString().trim().toLowerCase()

      const newMarkers = []
      const newBounds = new google.maps.LatLngBounds()

      const displayData =
        filtered.length === 0 && this.filtered.length > 0
          ? this.filtered
          : filtered

      const markerDataMap = this.markers.map((marker, i) => ({
        ...this.locations[i],
        marker
      }))

      markerDataMap.forEach((data) => {
        if (displayData.some((f) => isMatch(f, data))) {
          data.marker.setMap(this.map)
          newBounds.extend(data.marker.getPosition())
          newMarkers.push(data.marker)
        }
      })

      if (newMarkers.length > 0) {
        this.markerCluster = new markerClusterer.MarkerClusterer({
          markers: newMarkers,
          map: this.map
        })

        this.map.fitBounds(newBounds, this.getMapPadding())
        if (this.map.getZoom() > 15) this.map.setZoom(15)
      } else {
        console.warn('No markers to display from filtered data.')
      }
    })
  }
}

customElements.define('google-map-component', googleMap)
